import { formatFabulaUltimaCommandArgument } from './FabulaUltimaCommand';
import { calculateEnemyHpMp } from './FabulaUltimaDeepSeekPrompt';
import { isLegalDiceSize } from './FabulaUltimaGame';

export interface EnemyStats {
  name: string;
  maxHp: number;
  maxMp: number;
  dex: number;
  ins: number;
  mig: number;
  wlp: number;
  def: number;
  mDef: number;
  fixedDef: boolean;
}

export class EnemyInputError extends Error {
  constructor(readonly reason: 'json' | 'name' | 'duplicate' | 'integer' | 'dice' | 'rank', readonly field = '') {
    super(`${reason}: ${field}`);
  }
}

function integer(value: unknown, field: string, min = -2147483648): number {
  if ((typeof value !== 'number' && typeof value !== 'string')
    || (typeof value === 'string' && !/^-?\d+$/.test(value.trim()))) {
    throw new EnemyInputError('integer', field);
  }
  const result = Number(value);
  if (!Number.isInteger(result) || result < min || result > 2147483647) {
    throw new EnemyInputError('integer', field);
  }
  return result;
}

function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new EnemyInputError('json');
  return value as Record<string, unknown>;
}

/** Read the same nested NPC fields used by the existing natural-language importer. */
export function parseFultimatorEnemy(input: string): EnemyStats {
  let parsed: unknown;
  try { parsed = JSON.parse(input); } catch { throw new EnemyInputError('json'); }
  const data = object(parsed);
  const attributes = object(data.attributes);
  const extra = data.extra === undefined ? {} : object(data.extra);
  const rank = data.rank === undefined ? 'minion' : data.rank;
  if (typeof rank !== 'string' || !['minion', 'elite', 'champion2', 'champion3'].includes(rank)) {
    throw new EnemyInputError('rank');
  }
  const mig = integer(attributes.might, 'mig');
  const wlp = integer(attributes.will, 'wlp');
  const { hp, mp } = calculateEnemyHpMp({
    lvl: integer(data.lvl, 'lvl', 0), mig, wlp, rank,
    extra_hp: integer(extra.hp ?? 0, 'extra.hp'),
    extra_mp: integer(extra.mp ?? 0, 'extra.mp'),
  });
  if (extra.fixedDef !== undefined && typeof extra.fixedDef !== 'boolean') {
    throw new EnemyInputError('json');
  }
  return {
    name: typeof data.name === 'string' ? data.name : '',
    maxHp: hp, maxMp: mp, mig, wlp,
    dex: integer(attributes.dexterity, 'dex'), ins: integer(attributes.insight, 'ins'),
    def: integer(extra.def ?? 0, 'def'), mDef: integer(extra.mDef ?? 0, 'mDef'),
    fixedDef: extra.fixedDef === true,
  };
}

export function buildAddEnemyCommand(enemy: EnemyStats, existingNames: readonly string[] = []): string {
  if (!enemy.name.trim() || /[`\r\n]/.test(enemy.name)) throw new EnemyInputError('name');
  if (existingNames.includes(enemy.name)) throw new EnemyInputError('duplicate');
  const stats: string[] = [];
  for (const field of ['maxHp', 'maxMp', 'dex', 'ins', 'mig', 'wlp', 'def', 'mDef'] as const) {
    const value = integer(enemy[field], field, field === 'maxHp' ? 1 : field === 'maxMp' ? 0 : undefined);
    if (['dex', 'ins', 'mig', 'wlp'].includes(field) && !isLegalDiceSize(value)) {
      throw new EnemyInputError('dice', field);
    }
    stats.push(`${field}=${value}`);
  }
  return `addCreature ${formatFabulaUltimaCommandArgument(enemy.name)} enemy ${stats.join(' ')} fixedDef=${enemy.fixedDef}`;
}
